<template>
  <v-row no-gutters>
    <v-col cols="12">
      <ValidationObserver ref="observer">
        <div
          v-for="(beneficiary, index) in beneficiaries"
          :key="index"
          class="list-item"
        >
          <v-btn
            class="list-item-btn"
            :ripple="false"
            block
            color="white"
            @click="beneficiaryRemoved()"
          >
            <div class="list-item-text">
              <v-row no-gutters class="d-flex" align="center">
                <v-col cols="11">
                  <span class="list-item-name">{{
                    beneficiaries[index].beneficiaryDisplayName
                  }}</span>
                </v-col>
                <v-col cols="1">
                  <v-icon @click="beneficiaryRemoved()" class="cancel-icon">{{
                    icons.mdiCloseCircle
                  }}</v-icon>
                </v-col>
              </v-row>
            </div>
          </v-btn>
        </div>
      </ValidationObserver>

      <div class="mt-6">
        <AlternativeButton
          v-if="alternativeType === 'people' && !hasSelectedBeneficiary"
          :title="'Choose Person'"
          @gradientButtonClicked="showPersonDialog = true"
        />
        <AlternativeButton
          v-if="alternativeType === 'charity' && !hasSelectedBeneficiary"
          :title="'Choose Charity'"
          @gradientButtonClicked="showCharityDialog = true"
        />
        <AlternativeButton
          v-if="showAddTrust && !hasSelectedBeneficiary"
          :title="'Choose Trust'"
          @gradientButtonClicked="showTrustDialog = true"
        />
      </div>
    </v-col>

    <PersonDialog
      v-model="showPersonDialog"
      relation="HEIR"
      :create-new-relationship="false"
      :person-to-update="{}"
      :people-list-for-dialog="beneficiariesToSelectFrom"
      @existingPersonSelected="personSelected"
      @personCreated="personSelected"
    />
    <CharityDialog
      v-model="showCharityDialog"
      :existing-charities="beneficiariesToSelectFrom"
      @existingCharitySelected="charitySelected"
      @charityCreated="charitySelected"
    />
    <TrustDialog
      v-model="showTrustDialog"
      :existing-trusts="beneficiariesToSelectFrom"
      @existingTrustSelected="trustSelected"
      @trustCreated="trustSelected"
    />
  </v-row>
</template>

<script>
  import PersonDialog from '../people/PersonDialog.vue';
  import CharityDialog from '../people/CharityDialog.vue';
  import TrustDialog from '../people/TrustDialog.vue';
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';

  export default {
    name: 'ChooseAlternativeBeneficiary',
    components: {
      PersonDialog,
      CharityDialog,
      TrustDialog,
    },
    props: {
      beneficiaries: {
        type: Array,
        required: true,
      },
      beneficiariesToSelectFrom: {
        type: Array,
        required: true,
      },
      parentBeneficiary: {
        type: Object,
        required: true,
      },
      alternativeType: {
        type: String,
        required: true,
      },
      validateForm: {
        type: Boolean,
        required: true,
      },
    },
    data() {
      return {
        showPersonDialog: false,
        showCharityDialog: false,
        showTrustDialog: false,
        testator: {},
        totalPercentage: null,
        hasSelectedBeneficiary: false,
      };
    },
    computed: {
      peopleExcludingAlreadySelected() {
        if (!this.testator.people) {
          return undefined;
        }
        return this.testator.people.map(p => {
          return this.beneficiaries.find(
            b => b.beneficiaryType == 'Person' && p.id == b.beneficiaryId
          )
            ? { ...p, disabledMessage: 'Person already selected' }
            : p;
        });
      },
      charitiesExcludingAlreadySelected() {
        if (!this.testator.charities) {
          return undefined;
        }
        return this.testator.charities.map(p => {
          return this.beneficiaries.find(
            b => b.beneficiaryType == 'Charity' && p.id == b.beneficiaryId
          )
            ? { ...p, disabledMessage: 'Charity already selected' }
            : p;
        });
      },
      trustsExcludingAlreadySelected() {
        if (!this.testator.trusts) {
          return undefined;
        }
        return this.testator.trusts.map(p => {
          return this.beneficiaries.find(
            b => b.beneficiaryType == 'Trust' && p.id == b.beneficiaryId
          )
            ? { ...p, disabledMessage: 'Trust already selected' }
            : p;
        });
      },
      hideAddBeneficiaryBtns() {
        return (
          this.beneficiaries.length ||
          (this.beneficiaries.length &&
            this.beneficiaries[0].beneficiaryType != 'Person')
        );
      },
      showAddTrust() {
        return (
          this.testator.willCountry != 'GB' && this.alternativeType === 'trust'
        );
      },
    },
    created() {
      // using native JSON functions removes reactivity
      // so we can clone an object without mutating the original source
      if (this.beneficiary)
        this.chosenBeneficiary = JSON.parse(JSON.stringify(this.beneficiary));
    },
    watch: {
      validateForm: function (newValue) {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.$emit('beneficiariesValidated', true);
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      beneficiaries(newVal, oldVal) {
        if (newVal) {
          this.hasSelectedBeneficiary = false;
        } else {
          this.hasSelectedBeneficiary = true;
        }
      },
    },
    methods: {
      personSelected(person) {
        this.hasSelectedBeneficiary = true;
        this.beneficiarySelected(person, 'Person');
      },
      charitySelected(charity) {
        this.hasSelectedBeneficiary = true;
        this.beneficiarySelected(charity, 'Charity');
      },
      trustSelected(trust) {
        this.hasSelectedBeneficiary = true;
        this.beneficiarySelected(trust, 'Trust');
      },
      beneficiarySelected(beneficiary, beneficiaryType) {
        this.$emit('beneficiarySelected', {
          beneficiaryType: beneficiaryType,
          beneficiaryId: beneficiary.id,
          beneficiaryDisplayName: beneficiary.displayName,
          parentBeneficiaryId: this.parentBeneficiary.beneficiaryId,
        });
      },
      beneficiaryRemoved() {
        this.hasSelectedPerson = false;
        this.$emit('beneficiaryRemoved');
      },
      uniqueKey(beneficiary, index) {
        return `${beneficiary.beneficiaryId}-${index}`;
      },
    },
    apollo: {
      testator: {
        query: TESTATOR_QUERY,
        variables() {
          return {
            id: this.testatorId,
          };
        },
      },
    },
  };
</script>

<style scoped lang="scss">
  .list-item {
    max-width: 450px;
  }

  .add-item-btn {
    width: 100%;
    max-width: 450px;
  }

  .percentage-container {
    padding-top: 8px;
    padding-bottom: 8px;
    span {
      display: block;
      .v-text-field {
        padding-top: 0;
        margin-top: 0;
      }
    }
  }

  .percentage-container-normal {
    margin-top: 10px !important;
  }
  .percentage-container-error {
    margin-top: 20px !important;
  }

  .total-percentage-title {
    padding-top: 10px;
  }

  .total-percentage {
    background-color: #fbf398;
    border: 2px solid #fced56;
    border-radius: 10px;
    font-size: 18px;
    font-weight: bold;
    padding: 10px;
    text-align: center;
    width: 100%;
  }
  .total-percentage-container {
    margin-top: 30px;
    margin-bottom: 30px;
  }
</style>
